/**
 * The extra fields and follow-up questions on one category (or on the whole
 * desk). People filing under that category, or anything below it, see these
 * after the fields inherited from the levels above.
 */

import { useState } from 'react';
import { useDeleteField, useFields, useFormFields, useUpdateField } from '@/lib/useServiceDesk';
import type { SdFieldAdmin } from '@/lib/serviceDeskTypes';
import { FieldForm } from './FieldForm';
import { SdError } from './SdShell';
import { SD_BTN_GHOST, SD_BTN_PRIMARY, SD_MUTED } from './sdStyles';

export function FieldsPanel({ categoryId, title }: { categoryId: number | null; title: string }) {
  const fields = useFields(categoryId);
  const form = useFormFields(categoryId);
  const update = useUpdateField();
  const remove = useDeleteField();
  const [editing, setEditing] = useState<SdFieldAdmin | 'new' | null>(null);

  const list = [...(fields.data ?? [])].sort((a, b) => a.sort - b.sort);
  const nextSort = (list[list.length - 1]?.sort ?? 0) + 10;
  const all = form.data ?? [];

  async function move(f: SdFieldAdmin, delta: -1 | 1) {
    const i = list.findIndex((x) => x.id === f.id);
    const j = i + delta;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    const a = next[i];
    const b = next[j];
    if (!a || !b) return;
    next[i] = b;
    next[j] = a;
    for (let k = 0; k < next.length; k += 1) {
      const n = next[k];
      if (n && n.sort !== (k + 1) * 10) await update.mutateAsync({ id: n.id, patch: { sort: (k + 1) * 10 } });
    }
  }

  // Questions that come before the one being edited are the only ones it can depend on.
  const earlierFor = (f: SdFieldAdmin | null) => {
    if (!f) return all;
    const at = all.findIndex((x) => x.id === f.id);
    return at < 0 ? all : all.slice(0, at);
  };

  return (
    <section className="mt-5 border-t border-amp-line pt-4 dark:border-amp-dark-line" aria-label={title}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-amp-ink dark:text-amp-dark-ink">{title}</h3>
        {editing === null && (
          <button type="button" onClick={() => setEditing('new')} className={SD_BTN_PRIMARY}>
            Add a field
          </button>
        )}
      </div>
      <p className={`mb-3 text-[12px] ${SD_MUTED}`}>
        Fields people fill in when they file a ticket here, and follow-up questions that appear based on an earlier answer.
      </p>

      {fields.isLoading ? (
        <p className={`text-sm ${SD_MUTED}`}>Loading fields...</p>
      ) : list.length === 0 ? (
        <p className={`text-sm ${SD_MUTED}`}>No fields added here yet.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-amp-line dark:divide-amp-dark-line">
          {list.map((f, i) => (
            <li key={f.id} className="flex flex-wrap items-center gap-2 py-2 text-[13px]">
              <span className="min-w-0 flex-1">
                <span className="font-semibold text-amp-ink dark:text-amp-dark-ink">{f.label}</span>
                <span className={`ml-2 ${SD_MUTED}`}>
                  {f.field_type}
                  {f.required ? ', required' : ''}
                  {f.show_if ? `, shown when "${f.show_if.field_key}" is ${f.show_if.equals.join(' or ')}` : ''}
                </span>
              </span>
              <button type="button" disabled={i === 0} onClick={() => void move(f, -1)} className={SD_BTN_GHOST} aria-label={`Move ${f.label} up`}>
                Up
              </button>
              <button
                type="button"
                disabled={i === list.length - 1}
                onClick={() => void move(f, 1)}
                className={SD_BTN_GHOST}
                aria-label={`Move ${f.label} down`}
              >
                Down
              </button>
              <button type="button" onClick={() => setEditing(f)} className={SD_BTN_GHOST}>
                Edit
              </button>
              <button
                type="button"
                className={SD_BTN_GHOST}
                onClick={() => {
                  if (window.confirm(`Remove "${f.label}"? Old tickets keep the answers they already have.`)) remove.mutate(f.id);
                }}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {editing !== null && (
        <FieldForm
          key={editing === 'new' ? 'new' : editing.id}
          categoryId={categoryId}
          field={editing === 'new' ? null : editing}
          earlier={earlierFor(editing === 'new' ? null : editing)}
          nextSort={nextSort}
          onDone={() => setEditing(null)}
        />
      )}
      <SdError error={fields.error ?? remove.error ?? update.error} what="update the fields" />
    </section>
  );
}
